import { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { URL } from "../../store/utils";
import { handleSocketSessionExpiry } from "../../utils/dashboardUtils";
import useLatestToken, { attachTokenOnReconnect } from "../useLatestToken";
import logger from "../../utils/logger";

/**
 * Bootstrap kayitlari icin canli Socket.IO update'leri.
 *
 * Server (BayinerIOT-Server v1.7.3+) bootstrap state degisikliklerinde
 * "bootstrapRequestUpdate" event yayar — admin paneli polling beklemeden
 * yeni karti / status guncellemesini gorur.
 *
 * Mevcut akiş usePendingDevicesSocket ile ayni — kart bazli merge,
 * highlight (newItemIds), TTL ile silinme socket update degil API
 * polling/refetch ile yansir.
 */
export const usePendingBootstrapSocket = (token) => {
  const { hasToken, tokenRef } = useLatestToken(token);
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketItems, setSocketItems] = useState([]);
  const [newItemIds, setNewItemIds] = useState(new Set());
  const [removedIds, setRemovedIds] = useState(new Set());

  const merge = useCallback((doc) => {
    if (!doc || !doc._id) return;
    setSocketItems((prev) => {
      const idx = prev.findIndex((p) => p._id === doc._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = doc;
        return next;
      }
      return [doc, ...prev];
    });
    setNewItemIds((prev) => new Set(prev).add(doc._id));
    setTimeout(() => {
      setNewItemIds((prev) => {
        const next = new Set(prev);
        next.delete(doc._id);
        return next;
      });
    }, 600);
  }, []);

  const remove = useCallback((doc) => {
    if (!doc || !doc._id) return;
    setSocketItems((prev) => prev.filter((p) => p._id !== doc._id));
    setRemovedIds((prev) => new Set(prev).add(doc._id));
  }, []);

  useEffect(() => {
    if (!hasToken) return;

    if (
      socketRef.current &&
      (socketRef.current.connected || socketRef.current.connecting)
    ) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socketUrl = URL.url;

    socketRef.current = io(socketUrl, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      query: { token: tokenRef.current },
    });

    attachTokenOnReconnect(socketRef.current, tokenRef); // #142

    socketRef.current.on("connect", () => {
      setSocketConnected(true);
    });

    socketRef.current.on("connect_error", (err) => {
      logger.error("Bootstrap socket bağlantı hatası:", err.message);
    });

    socketRef.current.on("bootstrapRequestUpdate", (doc) => {
      merge(doc);
    });

    socketRef.current.on("bootstrapRequestRemove", (doc) => {
      remove(doc);
    });

    socketRef.current.on("disconnect", () => {
      setSocketConnected(false);
    });

    handleSocketSessionExpiry?.(socketRef.current);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [hasToken, tokenRef, merge, remove]);

  return { socketConnected, socketItems, newItemIds, removedIds };
};
